/**
 * frameReassembly — joins a frame the backend split into `chunked_message` slices.
 *
 * Web PubSub caps a message at 1 MB, so an oversized frame (in practice a large
 * `io.doclm.run.completed`) arrives as N envelopes:
 *
 * ```
 * { event_type: 'chunked_message', payload_id, chunk_index, total_chunks, data }
 * ```
 *
 * where each `data` is a slice of the original frame's JSON text. Joining the slices
 * in index order and parsing yields the frame exactly as it would have arrived whole.
 *
 * ## Why this runs per CONNECTION, not per chat
 * Ownership is decided by the frame's `chat_id` (see
 * [frameOwnership](../stream/frameOwnership.ts)), and a slice has no readable
 * `chat_id` until it is joined. So reassembly sits between decode and validation in
 * [ManagedConnection](./ManagedConnection.ts), and every chat downstream receives the
 * rebuilt frame as an ordinary one.
 *
 * ## What it guards against
 * - A lost slice: a payload with no new slice for `ttlMs` is evicted, so a partial
 *   payload cannot sit in memory for the life of the socket.
 * - Unbounded growth: at most `maxPending` payloads wait at once; the oldest yields.
 * - A redelivered slice (the reliable protocol can resend after a reconnect):
 *   overwriting the same index is idempotent.
 * - Slices that disagree on `total_chunks`, or carry an out-of-range index: the
 *   payload is discarded rather than joined with a hole in it.
 *
 * Pure and timer-free — expiry is swept on each `accept`, and `now` is injectable —
 * so it runs under the `__verify` harness with no socket.
 */

import { TRANSPORT_EVENT } from 'src/content/ChatV2/enums';
import { asRecord } from 'src/content/ChatV2/lib/guards';

/** How long a partial payload may go without a new slice before it is evicted. */
export const CHUNK_REASSEMBLY_TTL_MS = 60_000;

/** How many partial payloads one connection holds at once. */
export const MAX_PENDING_CHUNKED_PAYLOADS = 8;

/** Why a slice (or the payload it completed) could not produce a frame. */
export type ReassemblyDropReason = 'malformed' | 'inconsistent' | 'unparseable';

/** Why a partial payload was evicted before it completed. */
export type ReassemblyEvictReason = 'expired' | 'overflow';

/**
 * The outcome of one decoded frame:
 * - `frame` — pass it on (an ordinary frame, or a payload just completed);
 * - `pending` — a slice was stored, nothing to emit yet;
 * - `dropped` — a chunk envelope that cannot be used.
 */
export type ReassemblyResult =
  | { kind: 'frame'; frame: unknown }
  | { kind: 'pending' }
  | { kind: 'dropped'; reason: ReassemblyDropReason; payloadId?: string };

/** Details of a partial payload evicted before completing. */
export interface ReassemblyEviction {
  payloadId: string;
  reason: ReassemblyEvictReason;
  received: number;
  total: number;
}

export interface FrameReassemblerOptions {
  ttlMs?: number;
  maxPending?: number;
  now?: () => number;
  onEvict?: (eviction: ReassemblyEviction) => void;
}

export interface FrameReassembler {
  accept(decoded: unknown): ReassemblyResult;
  clear(): void;
  pendingCount(): number;
}

interface ChunkEnvelope {
  payloadId: string;
  index: number;
  total: number;
  data: string;
}

interface PendingPayload {
  total: number;
  slices: Map<number, string>;
  lastAt: number;
}

/** `true` when a decoded frame is a `chunked_message` envelope, well-formed or not. */
export function isChunkEnvelope(decoded: unknown): boolean {
  const envelope = asRecord(decoded);
  if (!envelope) {
    return false;
  }
  const type = envelope.event_type ?? envelope.event;
  return type === TRANSPORT_EVENT.CHUNKED_MESSAGE;
}

const readEnvelope = (
  decoded: Record<string, unknown>
): ChunkEnvelope | undefined => {
  const { payload_id, chunk_index, total_chunks, data } = decoded;
  const payloadIdOk =
    (typeof payload_id === 'string' && payload_id !== '') ||
    typeof payload_id === 'number';
  if (
    !payloadIdOk ||
    !Number.isInteger(total_chunks) ||
    !Number.isInteger(chunk_index) ||
    typeof data !== 'string'
  ) {
    return undefined;
  }
  const total = total_chunks as number;
  const index = chunk_index as number;
  if (total < 1 || index < 0 || index >= total) {
    return undefined;
  }
  return { payloadId: String(payload_id), index, total, data };
};

const join = (pending: PendingPayload): string => {
  let text = '';
  for (let i = 0; i < pending.total; i += 1) {
    text += pending.slices.get(i);
  }
  return text;
};

/** Create one reassembler. `ManagedConnection` owns exactly one per socket. */
export function createFrameReassembler(
  options: FrameReassemblerOptions = {}
): FrameReassembler {
  const ttlMs = options.ttlMs ?? CHUNK_REASSEMBLY_TTL_MS;
  const maxPending = options.maxPending ?? MAX_PENDING_CHUNKED_PAYLOADS;
  const now = options.now ?? Date.now;
  const pending = new Map<string, PendingPayload>();

  const evict = (payloadId: string, reason: ReassemblyEvictReason) => {
    const entry = pending.get(payloadId);
    if (!entry) {
      return;
    }
    pending.delete(payloadId);
    options.onEvict?.({
      payloadId,
      reason,
      received: entry.slices.size,
      total: entry.total
    });
  };

  const sweep = (at: number) => {
    pending.forEach((entry, payloadId) => {
      if (at - entry.lastAt > ttlMs) {
        evict(payloadId, 'expired');
      }
    });
  };

  const admit = (envelope: ChunkEnvelope, at: number): PendingPayload => {
    while (pending.size >= maxPending) {
      const oldest = pending.keys().next().value as string;
      evict(oldest, 'overflow');
    }
    const entry: PendingPayload = {
      total: envelope.total,
      slices: new Map(),
      lastAt: at
    };
    pending.set(envelope.payloadId, entry);
    return entry;
  };

  return {
    accept(decoded) {
      if (!isChunkEnvelope(decoded)) {
        return { kind: 'frame', frame: decoded };
      }
      const at = now();
      sweep(at);

      const envelope = readEnvelope(decoded as Record<string, unknown>);
      if (!envelope) {
        return { kind: 'dropped', reason: 'malformed' };
      }
      const { payloadId } = envelope;

      const existing = pending.get(payloadId);
      if (existing && existing.total !== envelope.total) {
        pending.delete(payloadId);
        return { kind: 'dropped', reason: 'inconsistent', payloadId };
      }
      const entry = existing ?? admit(envelope, at);
      entry.slices.set(envelope.index, envelope.data);
      entry.lastAt = at;

      if (entry.slices.size < entry.total) {
        return { kind: 'pending' };
      }

      pending.delete(payloadId);
      try {
        const frame = asRecord(JSON.parse(join(entry)));
        if (!frame) {
          return { kind: 'dropped', reason: 'unparseable', payloadId };
        }
        return { kind: 'frame', frame };
      } catch {
        return { kind: 'dropped', reason: 'unparseable', payloadId };
      }
    },

    clear() {
      pending.clear();
    },

    pendingCount() {
      return pending.size;
    }
  };
}
